import React, { useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../lib/adminAuth';

// Two-step login: enters username + password, if backend returns
// `requires_totp: true` we prompt for the 6-digit authenticator code and
// re-submit with the same credentials + code. JWT is only returned once both
// factors have been satisfied.
export default function AdminLoginGate({ onLoggedIn, title, subtitle }) {
  const [step, setStep] = useState('credentials'); // credentials | totp
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const attemptLogin = async (code) => {
    setSubmitting(true);
    setError('');
    try {
      const body = { username, password };
      if (code) body.totp_code = code;
      const { data } = await axios.post(`${API}/auth/admin-login`, body);
      if (data.requires_totp) {
        setStep('totp');
        return;
      }
      onLoggedIn(data.access_token);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.detail || 'Too many attempts. Try again later.');
      } else if (err.response?.status === 401) {
        setError(err.response.data.detail || 'Invalid credentials.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 'credentials') {
      attemptLogin();
    } else {
      attemptLogin(totpCode);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[#0A0A0A] px-4 py-20" data-testid="admin-login-gate">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 p-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">
          Restricted Area
        </span>
        <h1 className="text-3xl font-bold text-white mb-2">{title || 'Admin Sign-in'}</h1>
        <p className="text-sm text-zinc-400 mb-6">
          {step === 'credentials' ? (subtitle || 'Sign in with your admin credentials.') : 'Open your authenticator app and enter the 6-digit code.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          {step === 'credentials' && (
            <>
              <div>
                <Label htmlFor="admin-username" className="text-sm text-zinc-300">Username</Label>
                <Input
                  id="admin-username"
                  type="text"
                  autoComplete="username"
                  required
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="admin-username-input"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  placeholder="admin"
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-sm text-zinc-300">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="bg-zinc-900 border-zinc-700 text-white mt-1"
                  placeholder="••••••••"
                />
              </div>
            </>
          )}

          {step === 'totp' && (
            <div>
              <Label htmlFor="admin-totp" className="text-sm text-zinc-300">6-digit code</Label>
              <Input
                id="admin-totp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                data-testid="admin-totp-input"
                className="bg-zinc-900 border-zinc-700 text-white mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="••••••"
              />
              <button
                type="button"
                onClick={() => { setStep('credentials'); setTotpCode(''); setError(''); }}
                className="text-xs text-zinc-400 hover:text-white mt-2"
              >
                ← Use a different account
              </button>
            </div>
          )}

          {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit-button"
            className="w-full px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
          >
            {submitting ? 'Signing in…' : step === 'credentials' ? 'Sign in' : 'Verify code'}
          </button>
        </form>
      </div>
    </div>
  );
}
